import { DeesElement, customElement, html, css, cssManager, state } from '@design.estate/dees-element';
import { demoApp } from '../../../demos/templates.js';
import { themeDefaultStyles } from '../../00theme.js';
import type { DeesAppui } from './dees-appui.js';
import { GatewayDemoState, gatewayLayouts, gatewayViews, validateGatewayRoute, type TGatewayLayout } from '../../../demos/appui.state.js';
import { DeesDemoGatewayView } from '../../../demos/appui.views.js';
import { DeesModal } from '../../00group-overlay/dees-modal/dees-modal.js';
import type { IAppBarMenuItem } from '../../interfaces/index.js';
import '../../00group-input/dees-input-multitoggle/dees-input-multitoggle.js';

const layoutIcons: Record<string, string> = { browse: 'PanelLeft', inspect: 'PanelsTopLeft', focus: 'Maximize' };

@customElement('dees-demo-app-workspace')
export class DeesDemoAppWorkspace extends DeesElement {
  @state() public accessor workspace: GatewayDemoState | undefined;
  private searchModal?: DeesModal;
  // The menus carry the current layout as a checked choice, so they are rebuilt
  // whenever the workspace changes.
  private readonly changed = () => {
    this.requestUpdate();
    if (this.workspace) this.workspace.app.setAppBarMenus(this.appBarMenus(this.workspace));
  };
  public static styles = [themeDefaultStyles, cssManager.defaultStyles, css`
    :host { display: flex; flex-direction: column; gap: 12px; font: 13px/1.5 var(--dees-font-family); min-width: 0; }
    .demo-toolbar { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; justify-content: space-between; }
    .demo-toolbar p { margin: 0; font-size: 12px; color: var(--dees-color-text-secondary); }
    .presets { display: flex; align-items: center; gap: 12px; }
    .presets > span { font-size: 12px; font-weight: 500; color: var(--dees-color-text-secondary); }
    .frame { position: relative; height: 820px; max-height: calc(100dvh - 100px); min-height: 520px; overflow: hidden; border: 1px solid var(--dees-color-border-default); border-radius: var(--dees-radius-xl); }
    .map { color: var(--dees-color-text-secondary); font-size: 12px; }
    summary { cursor: pointer; padding: 4px 0; } summary:focus-visible { outline: 2px solid var(--dees-color-focus-ring); }
    .map-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px 24px; padding: 16px 0; }
    .map-grid p { margin: 0; } strong { color: var(--dees-color-text-primary); font-weight: 600; }
    @media (max-width: 600px) { .frame { max-height: none; height: 740px; } .demo-toolbar { align-items: flex-start; } }
  `];

  public render() {
    return html`<div class="demo-toolbar"><div class="presets"><span>Layout</span><dees-input-multitoggle type="single" label="Workspace layout" .labelPosition=${'none'} .options=${gatewayLayouts} .selectedOption=${this.workspace?.layout || 'browse'} @option-activate=${(event: CustomEvent<{ value: TGatewayLayout }>) => this.workspace?.setLayout(event.detail.value)}></dees-input-multitoggle></div><p>One gateway. Shared state. Local sample data.</p></div>
      <div class="frame"><dees-appui @gateway-connect=${(event: CustomEvent<{ view: DeesDemoGatewayView }>) => { event.detail.view.workspace = this.workspace; }}
        @appbar-search-click=${this.openSearch}
      ></dees-appui></div>
      <details class="map"><summary>Explore how the workspace fits together</summary><div class="map-grid">
        <p><strong>Browse / Inspect / Focus</strong><br>Try each layout without losing your open services, file draft, or selected chart.</p>
        <p><strong>Follow an incident</strong><br>Simulate latency on Overview, inspect edge-eu, then restore service. Metrics, badges, status, and activity follow.</p>
        <p><strong>Navigate at every level</strong><br>Use the main sidebar, service region filters, closable service tabs, breadcrumbs, and workspace search.</p>
        <p><strong>Work across components</strong><br>Edit a file, save a profile setting, compare five chart types, and read the resulting activity.</p>
      </div></details>`;
  }

  public async firstUpdated() {
    const app = this.shadowRoot!.querySelector('dees-appui') as DeesAppui;
    await app.updateComplete;
    const w = new GatewayDemoState(app); this.workspace = w;
    w.addEventListener('change', this.changed);
    app.setSecondaryMenuVisible(false); app.setContentTabsVisible(false);
    app.configure({
      branding: { logoIcon: 'lucide:Layers', logoText: 'Gateway' },
      appBar: {
        breadcrumbs: 'Gateway', breadcrumbSeparator: ' / ', showWindowControls: false, showSearch: true,
        location: true, locationBaseUrl: new URL(window.location.pathname, window.location.origin).href,
        user: { name: w.displayName, email: 'alex@gateway.example', status: 'online' },
        menuItems: this.appBarMenus(w),
        profileMenuItems: [
          { name: 'Profile settings', iconName: 'Settings', action: async () => { await w.navigate('settings'); } },
          { name: 'Toggle availability', iconName: 'Moon', action: async () => { w.busy = !w.busy; w.record(`Availability changed to ${w.busy ? 'busy' : 'online'}`); w.changed(); } },
          { divider: true }, { name: 'Workspace activity', iconName: 'Activity', action: async () => app.setActivityLogVisible(true) },
        ],
      },
      views: gatewayViews.map(view => ({ ...view, content: 'dees-demo-gateway-view', validateRoute: validateGatewayRoute })),
      mainMenu: { sections: [{ name: 'Production', views: ['overview', 'services', 'analytics', 'files'] }], bottomItems: ['settings'] },
      defaultView: 'overview',
      bottomBar: { widgets: [
        { id: 'health', label: 'All services healthy', iconName: 'lucide:CircleCheck', status: 'success', onClick: () => { void w.navigate('services'); }, contextMenuItems: [{ name: 'Run health check', action: () => w.refresh() }, { name: 'Open activity', action: () => app.setActivityLogVisible(true) }] },
        { id: 'sample', label: 'Sample 1', tooltip: 'The same sample is used throughout the workspace', position: 'right' },
      ], actions: [{ id: 'refresh', iconName: 'lucide:RefreshCw', tooltip: 'Refresh sample', onClick: () => w.refresh() }] },
      activityLog: { visible: false },
    });
    app.activityLog.addMany([
      { type: 'custom', user: 'Gateway', message: 'Health checks passed in Europe, North America, and Asia Pacific.', iconName: 'lucide:CircleCheck' },
      { type: 'update', user: 'Gateway', message: 'Configuration and incident runbooks are available in Files.' },
    ]);
  }
  /** The Window menu marks the current layout as a checked choice in one radio group. */
  private appBarMenus(w: GatewayDemoState): IAppBarMenuItem[] {
    const app = w.app;
    return [{ name: 'Gateway', action: async () => {}, submenu: [
      { name: 'Refresh sample', iconName: 'RefreshCw', action: async () => w.refresh() },
      { name: 'Simulate latency', iconName: 'Activity', action: async () => w.setHealth('degraded') },
      { name: 'Restore service', iconName: 'CircleCheck', action: async () => w.setHealth('recovered') },
      { divider: true }, { name: 'Workspace settings', iconName: 'Settings', action: async () => { await w.navigate('settings'); } },
    ] }, { name: 'File', action: async () => {}, submenu: [
      { name: 'Browse files', iconName: 'Folder', action: async () => { await w.navigate('files'); } },
      { name: 'Gateway configuration', iconName: 'FileCode', action: async () => { await app.navigateToView('files', {}, { key: 'gateway.json' }); } },
      { name: 'Incident runbook', iconName: 'BookOpen', action: async () => { await app.navigateToView('files', {}, { prefix: 'runbooks/', key: 'runbooks/latency.md' }); } },
    ] }, { name: 'View', action: async () => {}, submenu: [
      { name: 'Overview', iconName: 'LayoutDashboard', action: async () => { await w.navigate('overview'); } },
      { name: 'Services', iconName: 'Server', action: async () => { await w.navigate('services'); } },
      { name: 'Analytics', iconName: 'ChartNoAxesCombined', action: async () => { await w.navigate('analytics'); } },
      { divider: true }, { name: 'Refresh sample', iconName: 'RefreshCw', action: async () => w.refresh() },
    ] }, { name: 'Window', action: async () => {}, submenu: [
      ...gatewayLayouts.map(layout => ({
        name: `${layout.label} layout`, iconName: layoutIcons[layout.key], radioGroup: 'layout',
        checked: w.layout === layout.key, action: async () => w.setLayout(layout.key as TGatewayLayout),
      })),
      { divider: true },
      { name: 'Show activity', iconName: 'Activity', checked: app.getActivityLogVisible(),
        action: async () => { app.toggleActivityLog(); this.changed(); } },
    ] }];
  }

  private async openSearch() {
    if (this.searchModal?.isConnected) return;
    const modal = await DeesModal.createAndShow({ heading: 'Search Gateway', subheading: 'Jump to a workspace view or inspect a service.', width: 'medium', height: 460, menuOptions: [],
      content: html`<dees-demo-gateway-search .workspace=${this.workspace} .onChoose=${() => { void modal.destroy(); this.searchModal = undefined; }}></dees-demo-gateway-search>`,
    });
    this.searchModal = modal;
  }
  public async disconnectedCallback() {
    this.workspace?.removeEventListener('change', this.changed);
    void this.searchModal?.destroy(); await super.disconnectedCallback();
  }
}

export const demoFunc = () => demoApp(html`<dees-demo-app-workspace></dees-demo-app-workspace>`);
